import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { forkJoin } from 'rxjs';
import { GetLanguages_Result } from '@typeDefs/home/responses/home';
import { HomeApiService } from '@pages/home/services/home.api.service';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private readonly translocoService = inject(TranslocoService);
  private readonly homeApiService = inject(HomeApiService);
  private selectedLanguageCode = signal<string | null>(null);
  translationsLoaded = signal(false);

  availableLanguage = toSignal(
    this.homeApiService.getLanguages(),
    { initialValue: [] as GetLanguages_Result[] }
  );

  currentLanguage = computed(() => {
    const langs = this.availableLanguage();
    const code = this.selectedLanguageCode();
    if (!langs.length) return null;
    return langs.find(l => l.Code === code) || langs[0];
  });

  onLanguageChange(callback: (lang: GetLanguages_Result) => void) {
    effect(() => {
      const lang = this.currentLanguage();
      if (lang) callback(lang);
    });
  }

  onAvailableLanguagesLoaded(callback: (langs: GetLanguages_Result[]) => void) {
    effect(() => {
      const langs = this.availableLanguage();
      if (langs.length > 0) {
        callback(langs);
      }
    });
  }

  private readonly setDefaultLangEffect = effect(() => {
    const langs = this.availableLanguage();
    if (langs.length > 0 && !this.selectedLanguageCode()) {
      setTimeout(() => {
        this.selectedLanguageCode.set(langs[0].Code);
        this.translocoService.setActiveLang(langs[0].Code);
      });
    }
  });

  loadTranslations(basePaths: string[]) {
    const loads = basePaths.flatMap(basePath =>
      this.availableLanguage().map(lang =>
        this.translocoService.load(`${basePath}/${lang.Code}`)
      )
    );
    forkJoin(loads).subscribe(() => {
      this.translocoService.setActiveLang(this.currentLanguage().Code);
      this.translationsLoaded.set(true);
    });
  }

  setCurrentLanguage(lang: string) {
    this.selectedLanguageCode.set(lang);
    this.translocoService.setActiveLang(lang);
  }
}